import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  ExclamationCircleIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@hooks';
import type { LoginCredentials } from '@types';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Inline auth error region (design 3.1a). Server never says which field was
  // wrong, so the message stays deliberately generic.
  const [authError, setAuthError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>();

  const onSubmit = async (data: LoginCredentials) => {
    setIsLoading(true);
    setAuthError(null);
    try {
      await login(data);
      navigate('/dashboard');
    } catch {
      setAuthError('Email or password is incorrect');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-navy-900">
      {/* Atmospheric brand field */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(120% 90% at 18% 8%, #1b3f7d 0%, #0e2a5e 42%, #081a3d 100%)',
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(60% 55% at 82% 88%, rgba(56,189,248,.16), transparent 70%)',
        }}
      />
      {/* Oversized monogram watermark */}
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -left-10 select-none font-serif text-[420px] italic leading-[0.7] text-white/[0.035]"
      >
        L
      </div>

      {/* Brand lockup — top left */}
      <div className="absolute left-6 top-8 hidden items-center gap-3.5 sm:flex md:left-14">
        <div className="flex h-11 w-11 items-center justify-center rounded-[10px] border-[1.5px] border-white/50">
          <span className="font-serif text-[22px] italic text-white">L</span>
        </div>
        <div>
          <div className="font-serif text-[19px] text-white">
            Lionel J Photography
          </div>
          <div className="mt-0.5 font-mono text-[10.5px] uppercase tracking-[0.28em] text-[#8fb4e8]">
            Invoicing · Nassau, BS
          </div>
        </div>
      </div>

      {/* Tagline — lower left */}
      <div className="absolute bottom-14 left-6 hidden max-w-[420px] lg:block md:left-14">
        <p className="font-serif text-[30px] font-normal leading-[1.28] text-[#eaf1fb]">
          Every frame, every invoice —{' '}
          <span className="italic text-[#8fc4f5]">accounted for.</span>
        </p>
      </div>

      {/* Auth card */}
      <div className="relative flex min-h-screen items-center justify-center px-4">
        <div className="flex w-full max-w-[452px] flex-col gap-7 rounded-[20px] bg-white px-10 py-11 shadow-[0_24px_60px_rgba(6,20,48,.45)]">
          <div>
            <h1 className="font-serif text-[28px] font-medium tracking-tight text-gray-900">
              Sign in
            </h1>
            <p className="mt-1.5 text-sm text-gray-500">
              Welcome back. Access is by invitation only.
            </p>
          </div>

          {authError && (
            <div
              role="alert"
              className="flex items-start gap-3 rounded-[10px] border border-red-200 bg-red-50 px-3.5 py-3"
            >
              <ExclamationCircleIcon className="mt-px h-[18px] w-[18px] flex-shrink-0 text-red-600" />
              <div>
                <p className="text-[13px] font-semibold text-red-800">
                  {authError}
                </p>
                <p className="mt-0.5 text-[12.5px] text-red-500">
                  Check your details and try again.
                </p>
              </div>
            </div>
          )}

          <form
            className="flex flex-col gap-[18px]"
            onSubmit={handleSubmit(onSubmit)}
            data-testid="login-form"
            noValidate
          >
            <div>
              <label
                htmlFor="email"
                className="mb-1.5 block text-[13px] font-semibold text-gray-700"
              >
                Email address
              </label>
              <input
                id="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Invalid email address',
                  },
                })}
                type="email"
                autoComplete="username"
                aria-invalid={!!errors.email}
                className="h-[46px] w-full rounded-[10px] border-[1.5px] border-gray-300 px-3.5 text-sm text-gray-900 focus:border-primary-600 focus:ring-2 focus:ring-primary-600/20"
                placeholder="you@example.com"
              />
              {errors.email && (
                <p className="mt-1.5 text-[12px] text-red-600">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="mb-1.5 block text-[13px] font-semibold text-gray-700"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  {...register('password', {
                    required: 'Password is required',
                  })}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  aria-invalid={!!errors.password}
                  className="h-[46px] w-full rounded-[10px] border-[1.5px] border-gray-300 pl-3.5 pr-11 text-sm text-gray-900 focus:border-primary-600 focus:ring-2 focus:ring-primary-600/20"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={
                    showPassword ? 'Hide characters' : 'Show characters'
                  }
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-600/30"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-[18px] w-[18px]" />
                  ) : (
                    <EyeIcon className="h-[18px] w-[18px]" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-[12px] text-red-600">
                  {errors.password.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              data-testid="login-submit"
              className="h-12 w-full rounded-[10px] bg-primary-600 text-[15px] font-semibold text-white shadow-[0_6px_16px_rgba(37,99,235,.3)] hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="text-center text-[12.5px] leading-relaxed text-gray-400">
            Accounts are created by the owner.
            <br />
            Contact your administrator for access.
          </p>
        </div>
      </div>
    </div>
  );
}
